import { useState } from 'react';
import { Loader2, Play, Landmark, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function ProviderBadge({ configured }) {
  if (configured) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1">
        <CheckCircle2 className="h-3 w-3" />
        configured
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground gap-1">
      <XCircle className="h-3 w-3" />
      not configured
    </Badge>
  );
}

function ConnectedBadge({ connected }) {
  if (connected) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1">
        <CheckCircle2 className="h-3 w-3" />
        connected
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <XCircle className="h-3 w-3" />
      disconnected
    </Badge>
  );
}

function TokenBadge({ present, encrypted, label }) {
  if (!present) {
    return <span className="text-muted-foreground">absent</span>;
  }
  if (encrypted === true) {
    return <span className="text-green-600 dark:text-green-400">{label || 'encrypted'}</span>;
  }
  if (encrypted === null) {
    return (
      <span className="text-amber-600 dark:text-amber-400">
        present (plaintext?)
      </span>
    );
  }
  return <span className="text-amber-600 dark:text-amber-400">present</span>;
}

function LiveFeedsSection({ data }) {
  const { accounts_by_provider, total_accounts, provider_config } = data;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold" id="bank-inv-live-feeds">
        Live Bank Feeds
        <span className="text-xs font-normal text-muted-foreground ml-2">
          ({total_accounts} accounts across {Object.keys(accounts_by_provider).length} providers)
        </span>
      </h3>

      {/* Provider config summary */}
      <div className="flex flex-wrap gap-3">
        {provider_config.map((p) => (
          <div key={p.provider} className="flex items-center gap-2 text-xs">
            <span className="font-medium capitalize">{p.provider}:</span>
            <ProviderBadge configured={p.configured} />
            <span className="text-muted-foreground">{p.auth_method}</span>
          </div>
        ))}
      </div>

      {/* Accounts table per provider */}
      {Object.entries(accounts_by_provider).map(([provider, accounts]) => (
        <div key={provider} className="space-y-1">
          <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {provider} ({accounts.length} accounts)
          </h4>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-1.5 font-medium">Account ID</th>
                  <th className="text-left px-3 py-1.5 font-medium">Bank</th>
                  <th className="text-left px-3 py-1.5 font-medium">Name</th>
                  <th className="text-left px-3 py-1.5 font-medium">CCY</th>
                  <th className="text-left px-3 py-1.5 font-medium">Entity</th>
                  <th className="text-left px-3 py-1.5 font-medium">Last Synced</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.account_id} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono truncate max-w-[200px]" title={a.account_id}>
                      {a.account_id}
                    </td>
                    <td className="px-3 py-1.5">{a.bank_name}</td>
                    <td className="px-3 py-1.5">{a.account_name}</td>
                    <td className="px-3 py-1.5">{a.currency}</td>
                    <td className="px-3 py-1.5 font-mono">{a.entity_id}</td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {a.last_synced
                        ? new Date(a.last_synced).toLocaleString()
                        : 'never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {total_accounts === 0 && (
        <p className="text-xs text-muted-foreground">No live bank accounts found.</p>
      )}
    </div>
  );
}

function CredentialsSection({ data }) {
  const { connections_by_provider, total_connections } = data;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold" id="bank-inv-credentials">
        Credentials / Connection Store
        <span className="text-xs font-normal text-muted-foreground ml-2">
          ({total_connections} rows)
        </span>
      </h3>

      {Object.entries(connections_by_provider).map(([provider, rows]) => (
        <div key={provider} className="space-y-1">
          <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {provider} ({rows.length} {rows.length === 1 ? 'connection' : 'connections'})
          </h4>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-1.5 font-medium">Connection ID</th>
                  <th className="text-left px-3 py-1.5 font-medium">Entity</th>
                  <th className="text-left px-3 py-1.5 font-medium">Status</th>
                  <th className="text-left px-3 py-1.5 font-medium">Tokens</th>
                  <th className="text-left px-3 py-1.5 font-medium">Expiry</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={`${provider}-${c.connection_id}`} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono">{c.connection_id}</td>
                    <td className="px-3 py-1.5">{c.entity_label || c.entity_id || ''}</td>
                    <td className="px-3 py-1.5">
                      <ConnectedBadge connected={c.connected} />
                    </td>
                    <td className="px-3 py-1.5">
                      {provider === 'revolut' && (
                        <span className="space-x-2">
                          <span>access: <TokenBadge present={c.access_token_present} encrypted={c.access_token_encrypted} /></span>
                          <span>refresh: <TokenBadge present={c.refresh_token_present} encrypted={c.refresh_token_encrypted} /></span>
                        </span>
                      )}
                      {provider === 'bridge' && (
                        <span>
                          password: <TokenBadge present={c.password_present} encrypted={c.password_encrypted} />
                        </span>
                      )}
                      {provider === 'wise' && (
                        <span>
                          API key: {c.api_key_present
                            ? <span className="text-green-600 dark:text-green-400">present</span>
                            : <span className="text-muted-foreground">absent</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {c.expires_at
                        ? <>
                            {new Date(c.expires_at).toLocaleString()}
                            {c.token_expired && (
                              <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">expired</Badge>
                            )}
                          </>
                        : c.connected_at
                          ? `connected ${new Date(c.connected_at).toLocaleDateString()}`
                          : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function TransactionShapeSection({ data }) {
  const { collection, total_documents, by_source, reconciled, unreconciled, sample_fields, schema } = data;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold" id="bank-inv-tx-shape">
        Transaction Collection Shape
        <span className="text-xs font-normal text-muted-foreground ml-2">
          ({total_documents.toLocaleString()} documents in <code className="text-[11px]">{collection}</code>)
        </span>
      </h3>

      {/* Source distribution */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-3 py-1.5 font-medium">Source</th>
              <th className="text-right px-3 py-1.5 font-medium">Count</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(by_source).map(([src, count]) => (
              <tr key={src} className="border-b last:border-0">
                <td className="px-3 py-1.5 font-mono">{src}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reconciliation stats */}
      <div className="flex gap-4 text-xs">
        <span>
          <span className="font-medium">Reconciled:</span>{' '}
          <span className="tabular-nums">{reconciled.toLocaleString()}</span>
        </span>
        <span>
          <span className="font-medium">Unreconciled:</span>{' '}
          <span className="tabular-nums">{unreconciled.toLocaleString()}</span>
        </span>
      </div>

      {/* Schema details */}
      <div className="border rounded-lg p-3 space-y-2 text-xs">
        <div>
          <span className="font-medium">Entity key:</span>{' '}
          <span className="text-muted-foreground">{schema.entity_key}</span>
        </div>
        <div>
          <span className="font-medium">Idempotency key:</span>{' '}
          <span className="text-muted-foreground">{schema.idempotency_key}</span>
        </div>
        <div>
          <span className="font-medium">Idempotency mechanism:</span>{' '}
          <span className="text-muted-foreground font-mono">{schema.idempotency_mechanism}</span>
        </div>
        <div>
          <span className="font-medium">Indexes:</span>{' '}
          <span className="text-muted-foreground font-mono">{schema.indexes.join(', ')}</span>
        </div>
        <div>
          <span className="font-medium">Reconciliation:</span>{' '}
          <span className="text-muted-foreground">
            is_reconciled (bool) + matched_invoice_id (FK to invoices)
          </span>
        </div>
        {sample_fields.length > 0 && (
          <div>
            <span className="font-medium">Fields present in sample doc:</span>{' '}
            <span className="text-muted-foreground font-mono text-[11px]">
              {sample_fields.join(', ')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryBanner({ summary }) {
  return (
    <div className="border rounded-lg p-3 space-y-1.5 bg-muted/30">
      <h3 className="text-sm font-semibold">Integration Status Summary</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        {Object.entries(summary).map(([key, info]) => (
          <div key={key} className="flex items-start gap-2">
            {info.exists ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            )}
            <div>
              <span className="font-medium capitalize">{key.replace('_', ' ')}</span>
              <p className="text-muted-foreground">{info.scope}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BankConnectionsInventoryPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.bankConnectionsInventory();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Landmark className="h-5 w-5 text-blue-600" />
            Bank Connections Inventory
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Read-only inventory of every bank feed, credential row, and the transaction collection shape.
          Surfaces existing Revolut/BNP/Wise integrations and their scope.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-6">
          <SummaryBanner summary={result.summary} />
          <LiveFeedsSection data={result.live_feeds} />
          <CredentialsSection data={result.credentials_store} />
          <TransactionShapeSection data={result.transaction_shape} />
          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
